import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ActionIcon,
  Badge,
  Button,
  Chip,
  Flex,
  Loader,
  LoadingOverlay,
  Paper,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { ArrowLeft, HelpCircle, Percent, Redo, Undo } from "lucide-react";
import { useTranslation } from "react-i18next";
import { isEqual } from "lodash";

import PageContainer from "../../../../components/PageContainer";
import Schema from "../../../../components/Schema/Schema";
import { notification } from "../../../../utils/notification";

import { useAppSelector } from "../../../../app/hooks";
import {
  useLazyReadSchemeQuery,
  useUpdateSchemeMutation,
} from "../../../../api/scheme";
import { isErrorWithMessage, showErrorMessages } from "../../../../app/api";

import ContextMenu from "./contextMenu";
import HelpModal from "../modal/HelpModal";

import SetSector from "./panel-options/set-sector/SetSector";
import ModeTogglePanel from "./panel-options/ModeTogglePanel";
import { SectorProperties, TextToInsert } from "./constants/interface";
import TextOptions from "./panel-options/insert-edit-text/TextOptions";
import SelectionMode from "./panel-options/selection-mode/selectionMode";
import InsertAdditionalSeats from "./panel-options/insert-additional-seats/insertAdditionalSeats";

import {
  emptySelectedKeys,
  hallPartsNames,
  initialContextMenu,
  initialEditorModes,
  initialSelectedKeys,
  initialTextToInsert,
} from "./constants";
import { SelectionSectorType } from "./constants/enum";
import {
  ContextMenuState,
  EditorModes,
  HallPartOption,
  SelectedKeys,
} from "./constants/types";

import { useMouseMove } from "./utils/mouse-move";
import { HandleOnSeatClick, handleSchemaActionClick } from "./utils/sector";
import { handleRedo, handleUndo } from "./utils/history";
import InsertShape from "./panel-options/insert-shape/insertShape";
import FanZone from "./panel-options/fan-zone/FanZone";

const SchemeViewEdit: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const roles = useAppSelector((state) => state.auth.user.role);
  const canEditScheme = roles?.some((role) =>
    ["administrators"].includes(role)
  );
  const schemaRef = useRef<HTMLDivElement | null>(null);
  const schema_px = "15px";

  // API
  const [readScheme] = useLazyReadSchemeQuery();
  const [updateScheme] = useUpdateSchemeMutation();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>(null);
  const [zones, setZones] = useState<any>(null);

  const [widgetMode, setWidgetMode] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(100);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [blockSelection, setBlockSelection] = useState<boolean>(true);

  const [text, setText] = useState<any>(null);
  const [textToInsert, setTextToInsert] =
    useState<TextToInsert>(initialTextToInsert);

  const [editorModes, setEditorModes] =
    useState<EditorModes>(initialEditorModes);
  const disableAllEditorModes = () => {
    setEditorModes((prev) => {
      const updated = Object.fromEntries(
        Object.keys(prev).map((key) => [
          key,
          { ...prev[key as keyof EditorModes], enabled: false },
        ])
      );
      return updated as EditorModes;
    });

    setSelectedKey("");
    deSelectAll();
  };
  const handleDotInsertMode = () => {
    const targetKey = "null:1:1";
    const newData = { ...data, data: { ...data.data } };

    // Remove any key that starts with "null:"
    for (const key of Object.keys(newData.data)) {
      if (key.startsWith("null:")) {
        delete newData.data[key];
      }
    }

    // Insert the new seat with null sector
    newData.data[targetKey] = { rect: { x: 0, y: 20 } };
    setData(newData);
    setSelectedKey(targetKey);
  };

  const [undoStack, setUndoStack] = useState<any[]>([]);
  const [redoStack, setRedoStack] = useState<any[]>([]);
  const [selectedKey, setSelectedKey] = useState<string>("");
  const [sectorProperties, setSectorProperties] = useState<SectorProperties>({
    readonly: true,
  });

  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const openHelpModal = () => setIsHelpModalOpen(true);
  const closeHelpModal = () => setIsHelpModalOpen(false);
  const [contextMenu, setContextMenu] =
    useState<ContextMenuState>(initialContextMenu);

  const [selectionSectorNameChange, setSelectionSectorNameChange] =
    useState<HallPartOption | null>(null);
  const [selectionSectorType, setSelectionSectorType] = useState<string>(
    SelectionSectorType.ENTIRE
  );
  const [selectedDots, setSelectedDots] = useState<Record<string, any>>([]);
  const [selectedTextFigures, setSelectedTextFigures] = useState<
    Record<string, any>
  >([]);
  const [selectedFanZones, setSelectedFanZones] = useState<Record<string, any>>(
    []
  );
  const [selectedKeys, setSelectedKeys] =
    useState<SelectedKeys>(initialSelectedKeys);

  const handleMouseClick = (e: React.MouseEvent<SVGSVGElement>) => {
    if (contextMenu) {
      setContextMenu({ visible: false, x: 0, y: 0 });
    }

    if (textToInsert.properties.text !== "") {
      setTextToInsert(initialTextToInsert);
    }

    handleSchemaActionClick(
      e,
      data,
      editMode,
      undoStack,
      setUndoStack,
      setData,
      setSelectedKeys,
      setText,
      editorModes,
      textToInsert,
      setTextToInsert
    );
  };

  const handleClickEventOnText = (e: React.MouseEvent<SVGTextElement>) => {
    const target = e.target as SVGTextElement;
    const id = target.getAttribute("id");

    deSelectAll();
    setSelectedTextFigures([`text:${id}`]);

    setSelectedKey("");
    setSelectedKeys((prevState) => ({
      ...prevState,
      selectedKeyText: id!.toString(),
    }));
  };

  const handleClickEventOnPolyline = (e: React.MouseEvent<SVGTextElement>) => {
    const target = e.target as SVGTextElement;
    const id = target.getAttribute("id");

    deSelectAll();
    setSelectedTextFigures([`polyline:${id}`]);

    setSelectedKey("");
    setSelectedKeys((prevState) => ({
      ...prevState,
      selectedKeyPolyline: id!.toString(),
    }));
  };

  const handleClickEventOnRect = (e: React.MouseEvent<SVGTextElement>) => {
    const target = e.target as SVGTextElement;
    const id = target.getAttribute("id");

    deSelectAll();
    setSelectedTextFigures([`rect:${id}`]);

    setSelectedKey("");
    setSelectedKeys((prevState) => ({
      ...prevState,
      selectedKeyRect: id!.toString(),
    }));
  };

  const handleClickEventOnFanZone = (e: React.MouseEvent<SVGTextElement>) => {
    const target = e.target as SVGTextElement;
    const id = target.getAttribute("id");
    deSelectAll();
    setSelectedKey("");

    setSelectedFanZones([`fan-zone:${id}`]);
    setSelectedKeys((prevState) => ({
      ...prevState,
      selectedKeyFanZone: id!.toString(),
    }));
  };

  const handleClickEventOnCircle = (e: React.MouseEvent<SVGTextElement>) => {
    const target = e.target as SVGTextElement;
    const id = target.getAttribute("id");

    deSelectAll();
    setSelectedTextFigures([`circle:${id}`]);

    setSelectedKey("");

    setSelectedKeys((prevState) => ({
      ...prevState,
      selectedKeyCustomCircle: id!.toString(),
    }));
  };

  const onSeatClick = useCallback(
    (id: any, key: any) => {
      if (selectedTextFigures.length > 0 || selectedFanZones.length > 0) {
        deSelectAll();
      }

      HandleOnSeatClick(
        id,
        key,
        editMode,
        editorModes,
        data,
        setData,
        sectorProperties,
        selectionSectorType,
        setSectorProperties,
        setSelectedKey,
        setSelectedDots,
        undoStack,
        setUndoStack,
        deSelectAll
      );
    },
    [
      selectedTextFigures.length,
      editMode,
      editorModes,
      data,
      sectorProperties,
      selectionSectorType,
      undoStack,
    ]
  );

  const handleUpdateScheme = async () => {
    deSelectAll();

    try {
      const payload = {
        data: data.data,
        properties: data.properties,
        hall_parts_data: data.hall_parts_data,
      };
      await updateScheme({
        id: data.id,
        data: payload,
      }).unwrap();

      notification({
        type: "success",
        title: t("scheme.notifications.updated"),
      });
    } catch (e) {
      if (isErrorWithMessage(e)) {
        showErrorMessages(e.data);
      }
      console.error("Error updating scheme:", e);
    }
  };

  const handleSelection = (tmpSelection: string[]) => {
    if (selectedDots) {
      deSelectAll();
    }
    let updatedSelection = tmpSelection.filter(
      (item) => item !== "selectionArea" && item !== "rect:selectionArea"
    );

    const dots = updatedSelection.filter(
      (item) =>
        !item.startsWith("text") &&
        !item.startsWith("polyline") &&
        !item.startsWith("rect") &&
        !item.startsWith("circle:")
    );
    const textFigures = updatedSelection.filter(
      (item) =>
        item.startsWith("text") ||
        item.startsWith("polyline") ||
        item.startsWith("rect") ||
        item.startsWith("circle")
    );
    const fanZones = updatedSelection.filter((item) =>
      item.startsWith("fan-zone:fz-")
    );

    setSelectedDots([...dots]);
    setSelectedTextFigures([...textFigures]);
    setSelectedFanZones([...fanZones]);

    if (selectionSectorNameChange) {
      setUndoStack([...undoStack, data]);

      setData((prevData: { data: Record<string, any> }) => ({
        ...prevData,
        data: Object.fromEntries(
          Object.entries(prevData.data).map(([key, value]) => {
            const [, indexRow, indexCol] = key.split(":");
            if (dots.some((dot) => dot === key)) {
              const updatedKey = `${selectionSectorNameChange.value}:${indexRow}:${indexCol}`;
              return [updatedKey, value];
            }

            return [key, value];
          })
        ),
      }));
      setSelectedKey("");

      notification({ type: "success", title: "Sector changed" });
      return;
    } else {
      setData((prevData: { data: Record<string, any> }) => ({
        ...prevData,
        data: Object.fromEntries(
          Object.entries(prevData.data).map(([key, value]) => {
            const updatedValue = { ...value };
            if (dots.includes(key)) {
              updatedValue.selected = true;
            }
            return [key, updatedValue];
          })
        ),
      }));

      return;
    }
  };

  const deSelectAll = () => {
    if (Object.keys(selectedDots).length > 0) {
      setData((prevData: { data: Record<string, any> }) => ({
        ...prevData,
        data: Object.fromEntries(
          Object.entries(prevData.data).map(([key, value]) => {
            const updatedValue = { ...value };
            delete updatedValue.selected;
            return [key, updatedValue];
          })
        ),
      }));

      setSelectedDots({});
    }

    setSelectedTextFigures({});
    setSelectionSectorNameChange(null);
    setSelectedKeys(emptySelectedKeys);
    setSelectedFanZones([]);
  };

  const handleRightClick = (e: React.MouseEvent) => {
    e.preventDefault();

    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
    });
  };

  const closeContextMenu = () => {
    setContextMenu({ visible: false, x: 0, y: 0 });
  };

  const handleScaleChange = (newScale: number) => {
    setZoomLevel(newScale * 100);
  };

  useEffect(() => {
    localStorage.setItem("hallPartsNames", JSON.stringify(hallPartsNames));
  }, []);

  useEffect(() => {
    const fetchSchemeData = async () => {
      if (!id) return;

      setLoading(true);
      try {
        const schemeResponse = await readScheme({ id }).unwrap();
        setData(schemeResponse);
        setZones(schemeResponse.hall_parts);
        setText(schemeResponse.properties.text);
      } catch (error) {
        if (isErrorWithMessage(error)) {
          showErrorMessages(error.data);
        }
        console.error("Error fetching scheme data:", error);
      } finally {
        setLoading(false);
      }
    };

    void fetchSchemeData();
  }, [id, readScheme]);

  // UseEffect to call generation of a sector
  const previousSectorPropertiesRef = useRef<SectorProperties | null>(null);
  useEffect(() => {
    const manuallyTriggerSeatClick = () => {
      onSeatClick(0, selectedKey);
    };

    // Avoid duplicate calls by checking deep equality
    if (
      !isEqual(previousSectorPropertiesRef.current, sectorProperties) &&
      editorModes.dotInsert
    ) {
      console.warn("Call manuallyTriggerSeatClick for Inserting Sector");
      manuallyTriggerSeatClick();
      previousSectorPropertiesRef.current = sectorProperties;
    }
  }, [sectorProperties, editorModes.dotInsert, selectedKey, onSeatClick]);

  useMouseMove(
    schemaRef,
    editMode,
    editorModes.selectItem.enabled,
    data,
    setData,
    setText,
    selectedDots,
    selectedTextFigures,
    selectedFanZones,
    setBlockSelection
  );

  return (
    <PageContainer>
      <HelpModal isOpen={isHelpModalOpen} onClose={closeHelpModal} />

      {editMode && contextMenu.visible && (
        <ContextMenu
          undoStack={undoStack}
          setUndoStack={setUndoStack}
          data={data}
          setData={setData}
          text={text}
          setText={setText}
          contextMenu={contextMenu}
          setContextMenu={setContextMenu}
          editMode={editMode}
          selectedDots={selectedDots}
          setSelectedDots={setSelectedDots}
          selectedTextFigures={selectedTextFigures}
          setSelectedTextFigures={setSelectedTextFigures}
          t={t}
          closeContextMenu={closeContextMenu}
          deSelectAll={deSelectAll}
          selectedKeys={selectedKeys}
          selectedFanZones={selectedFanZones}
        />
      )}

      <Paper
        py={15}
        px={20}
        style={{
          borderBottomLeftRadius: "0px",
          borderBottomRightRadius: "0px",
          border: "2px solid #2c2e33",
          borderBottom: "none",
        }}
      >
        <Flex w={"100%"} justify={"space-between"}>
          <Flex gap={10} align={"center"}>
            <ActionIcon p={2} onClick={() => navigate("/dictionary/schemes/")}>
              <ArrowLeft size={17} />
            </ActionIcon>

            <Title order={4}>{data?.name || <Loader />}</Title>
          </Flex>

          <Button
            variant="default"
            radius={0}
            px={10}
            onClick={!isHelpModalOpen ? openHelpModal : closeHelpModal}
          >
            <Text mr={5}>{t("scheme.redactor.help.btnTitle")}</Text>
            <HelpCircle size={18} />
          </Button>
        </Flex>
      </Paper>

      <Flex justify={"space-between"}>
        <Paper
          w={editMode ? "82%" : "100%"}
          radius={0}
          style={{
            border: "2px solid #2c2e33",
          }}
        >
          <Flex justify={"space-between"} p={15}>
            <Flex gap={10} align={"center"}>
              {canEditScheme && (
                <Chip
                  color={editMode ? "red" : "blue"}
                  onClick={() => {
                    if (editMode) {
                      setBlockSelection(true);
                      setEditMode(false);
                      deSelectAll();
                      setSelectedKey("");
                      setSelectedTextFigures([]);
                      setSelectedDots({});
                    } else {
                      setEditMode(true);
                      setBlockSelection(false);
                      setContextMenu({ visible: false, x: 0, y: 0 });

                      disableAllEditorModes();
                      setEditorModes((prev) => ({
                        ...prev,
                        selectItem: {
                          ...prev.selectItem,
                          enabled: !prev.selectItem.enabled,
                        },
                      }));
                    }
                  }}
                  variant="filled"
                >
                  {editMode
                    ? t("scheme.redactor.buttons.quitEditMode")
                    : t("scheme.redactor.buttons.enterEditMode")}
                </Chip>
              )}

              {editMode && (
                <>
                  <Button size="xs" onClick={handleUpdateScheme}>
                    {t("scheme.saveScheme")}
                  </Button>
                </>
              )}
            </Flex>

            <Flex justify={"space-between"} align={"center"} gap={10}>
              {editMode ? (
                <Flex gap={10} align={"center"}>
                  <Tooltip label={t("scheme.redactor.buttons.undo")}>
                    <ActionIcon
                      p={2}
                      onClick={() => {
                        handleUndo(
                          setUndoStack,
                          setRedoStack,
                          setData,
                          setText,
                          undoStack,
                          data
                        );
                      }}
                      disabled={undoStack.length === 0}
                    >
                      <Undo />
                    </ActionIcon>
                  </Tooltip>

                  <Tooltip label={t("scheme.redactor.buttons.redo")}>
                    <ActionIcon
                      p={2}
                      onClick={() =>
                        handleRedo(
                          setUndoStack,
                          setRedoStack,
                          setData,
                          setText,
                          undoStack,
                          redoStack,
                          data
                        )
                      }
                      disabled={redoStack.length === 0}
                    >
                      <Redo />
                    </ActionIcon>
                  </Tooltip>

                  <Flex>
                    <Badge size="lg" rightSection={<Percent size={17} />}>
                      {Math.round(zoomLevel)}
                    </Badge>
                  </Flex>
                </Flex>
              ) : null}
            </Flex>
          </Flex>

          <Flex>
            {editMode && (
              <ModeTogglePanel
                t={t}
                editorModes={editorModes}
                setEditorModes={setEditorModes}
                disableAllEditorModes={disableAllEditorModes}
                handleDotInsertMode={handleDotInsertMode}
                setBlockSelection={setBlockSelection}
              />
            )}

            <div
              ref={schemaRef}
              style={{
                width: "100%",
                height: "90vh",
                position: "relative",
              }}
            >
              <LoadingOverlay visible={loading} zIndex={10} />

              <Schema
                data={data}
                text={text}
                zones={zones}
                blockSelection={blockSelection}
                type="price"
                showHints={true}
                onSelectionArea={editMode ? handleSelection : () => {}}
                onClick={handleMouseClick}
                onSeatClick={onSeatClick}
                onFanZoneClick={
                  editorModes.selectItem.enabled
                    ? handleClickEventOnFanZone
                    : (): void => {}
                }
                onCircleClick={
                  editorModes.selectItem.enabled
                    ? handleClickEventOnCircle
                    : (): void => {}
                }
                onPolylineClick={
                  editorModes.selectItem.enabled
                    ? handleClickEventOnPolyline
                    : (): void => {}
                }
                onRectClick={
                  editorModes.selectItem.enabled
                    ? handleClickEventOnRect
                    : (): void => {}
                }
                onTextClick={
                  editorModes.selectItem.enabled
                    ? handleClickEventOnText
                    : (): void => {}
                }
                viewBox="0 0 1030 750"
                widgetMode={widgetMode}
                styles={{
                  backgroundSize: `${Math.round(zoomLevel)}px ${Math.round(
                    zoomLevel
                  )}px`,
                  backgroundImage: `linear-gradient(to right, rgba(128, 128, 128, 0.2) 1px, transparent 1px),
                                                  linear-gradient(to bottom, rgba(128, 128, 128, 0.2) 1px, transparent 1px)`,
                }}
                onRightClick={handleRightClick}
                showOnly={"sold"}
                onScaleChange={handleScaleChange}
              />
            </div>
          </Flex>
        </Paper>

        {editMode && (
          <Paper
            w={"18%"}
            radius={0}
            style={{
              border: "2px solid #2c2e33",
              borderLeft: "none",
            }}
          >
            <Title
              style={{ background: "#2c2e33" }}
              px={15}
              py={10}
              mb={15}
              order={3}
            >
              {t("scheme.redactor.panelOptions.title")}
            </Title>

            <Chip
              px={15}
              checked={widgetMode}
              onClick={() => {
                if (widgetMode) {
                  setWidgetMode(false);
                } else {
                  setWidgetMode(true);
                }
              }}
            >
              {widgetMode
                ? t("scheme.redactor.panelOptions.widgetMode.on")
                : t("scheme.redactor.panelOptions.widgetMode.of")}
            </Chip>

            {editorModes.selectItem.enabled && (
              <SelectionMode
                t={t}
                px={"15px"}
                selectionSectorType={selectionSectorType}
                setSelectionSectorType={setSelectionSectorType}
                setSelectionSectorNameChange={setSelectionSectorNameChange}
              />
            )}

            {editorModes.insertSeatInRowOrCol.enabled && (
              <InsertAdditionalSeats
                selectedKey={selectedKey}
                setSelectedKey={setSelectedKey}
                px={schema_px}
                setData={setData}
                data={data}
                setUndoStack={setUndoStack}
                t={t}
              />
            )}

            {selectedKey && (
              <SetSector
                selectionSectorType={selectionSectorType}
                px={schema_px}
                undoStack={undoStack}
                setUndoStack={setUndoStack}
                data={data}
                setData={setData}
                selectedDots={selectedDots}
                sectorProperties={sectorProperties}
                setSectorProperties={setSectorProperties}
                t={t}
              />
            )}

            {(editorModes.textInsert.enabled ||
              selectedKeys.selectedKeyText !== "") && (
              <TextOptions
                px={schema_px}
                data={data}
                setData={setData}
                setText={setText}
                textInsertMode={editorModes.textInsert}
                selectedText={selectedKeys.selectedKeyText}
                setSelectedKeys={setSelectedKeys}
                textToInsert={textToInsert}
                setTextToInsert={setTextToInsert}
                t={t}
              />
            )}

            {(editorModes.shapeInsert.enabled ||
              selectedKeys.selectedKeyPolyline !== "" ||
              selectedKeys.selectedKeyRect !== "" ||
              selectedKeys.selectedKeyCustomCircle !== "") && (
              <InsertShape
                px={schema_px}
                selectedKeys={selectedKeys}
                setSelectedKeys={setSelectedKeys}
                undoStack={undoStack}
                setUndoStack={setUndoStack}
                data={data}
                setData={setData}
                setText={setText}
                t={t}
              />
            )}

            {(editorModes.fanZone.enabled ||
              selectedKeys.selectedKeyFanZone !== "") && (
              <FanZone
                selectedKeyFanZone={selectedKeys.selectedKeyFanZone}
                setUndoStack={setUndoStack}
                data={data}
                undoStack={undoStack}
                px={schema_px}
                setData={setData}
                t={t}
              />
            )}
          </Paper>
        )}
      </Flex>
    </PageContainer>
  );
};

export default SchemeViewEdit;
